import React, { useState } from 'react';

export default function StandaloneScriptGenerator({ user, onCreditsUpdate }) {
  const [topic, setTopic] = useState('');
  const [format, setFormat] = useState('full');
  const [duration, setDuration] = useState('8-10 min');
  const [loading, setLoading] = useState(false);
  const [script, setScript] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return;

    setLoading(true);
    setError('');
    setScript('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/analysis/generate-script', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ topic, format, duration })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate AI script.');
      }

      if (data.success && data.script) {
        setScript(data.script);
        if (data.credits?.remaining !== undefined && onCreditsUpdate) {
          onCreditsUpdate(data.credits.remaining);
        }
      } else {
        throw new Error('Could not generate script for this topic.');
      }
    } catch (err) {
      console.error('Script Generation Error:', err);
      setError(err.message || 'Error connecting to AI server.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!script) return;
    navigator.clipboard.writeText(script);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!script) return;
    const blob = new Blob([script], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `script_${topic.toLowerCase().replace(/[^a-z0-9]/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', color: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '30px', margin: '0 0 10px 0', background: 'linear-gradient(90deg, #ec4899, #8b5cf6, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
          ✍️ AI YouTube Script Writer
        </h2>
        <p style={{ color: '#94a3b8', margin: 0, fontSize: '15px' }}>
          Generate engaging, high-retention video script outlines & hooks tailored for YouTube algorithms.
        </p>
      </div>

      {/* Input Form */}
      <form onSubmit={handleGenerate} style={{ background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(16px)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', marginBottom: '30px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 180px auto', gap: '15px', alignItems: 'end' }}>
          
          <div>
            <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Topic / Keyword</label>
            <input 
              type="text" 
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. How to Monetize YouTube Shorts in 2026..."
              style={{ width: '100%', padding: '14px 18px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.12)', background: 'rgba(0, 0, 0, 0.3)', color: '#ffffff', fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
              required 
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Content Format</label>
            <select 
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              style={{ width: '100%', padding: '14px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.12)', background: '#0f111a', color: '#ffffff', fontSize: '14px', outline: 'none', cursor: 'pointer' }}
            >
              <option value="full">🎥 Full Video</option>
              <option value="short">⚡ Shorts / Reel</option>
              <option value="tutorial">📚 Step-by-Step</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' }}>Target Duration</label>
            <select 
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              style={{ width: '100%', padding: '14px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.12)', background: '#0f111a', color: '#ffffff', fontSize: '14px', outline: 'none', cursor: 'pointer' }}
            >
              <option value="1-3 min">1-3 Minutes</option>
              <option value="5-8 min">5-8 Minutes</option>
              <option value="8-10 min">8-10 Minutes</option>
              <option value="12+ min">12+ Minutes</option>
            </select>
          </div>

          <button 
            type="submit" 
            disabled={loading || !topic.trim()}
            style={{ padding: '14px 24px', borderRadius: '10px', background: (loading || !topic.trim()) ? '#334155' : 'linear-gradient(90deg, #ec4899, #8b5cf6)', color: 'white', border: 'none', cursor: (loading || !topic.trim()) ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '15px', height: '48px', transition: 'all 0.3s', boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)' }}
          >
            {loading ? '⏳ Writing Script...' : '✨ Generate (15 Credits)'}
          </button>

        </div>
      </form>

      {/* Error Message */}
      {error && (
        <div style={{ background: 'rgba(239, 68, 68, 0.15)', borderLeft: '4px solid #ef4444', color: '#fca5a5', padding: '16px', borderRadius: '10px', marginBottom: '30px', fontSize: '15px' }}>
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      {/* Script Display */}
      {script && (
        <div style={{ background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(16px)', borderRadius: '16px', padding: '24px', border: '1px solid rgba(255, 255, 255, 0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', animation: 'fadeIn 0.5s ease-in' }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '15px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📜 Generated Script Outline — <span style={{ color: '#ec4899' }}>{topic}</span>
            </h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={handleCopy}
                style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(139, 92, 246, 0.2)', color: '#c4b5fd', border: '1px solid rgba(139, 92, 246, 0.3)', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
              >
                {copied ? '✅ Copied!' : '📋 Copy Script'}
              </button>
              <button 
                onClick={handleDownload}
                style={{ padding: '8px 16px', borderRadius: '8px', background: 'rgba(59, 130, 246, 0.2)', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
              >
                📥 Download TXT
              </button>
            </div>
          </div>

          <div style={{ background: 'rgba(0, 0, 0, 0.4)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)' }}>
            <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', fontFamily: "'Fira Code', monospace, sans-serif", fontSize: '14px', lineHeight: '1.6', color: '#e2e8f0', margin: 0 }}>
              {script}
            </pre>
          </div>

        </div>
      )}

    </div>
  );
}
